import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RingLoader } from 'react-spinners';
import { Button, Section } from '../../design-system';
import MarketingLayout from '../layouts/MarketingLayout';
import FadeIn from '../components/FadeIn';
import { getNetworkScreens } from '../../services/getNetworkScreens';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

function screenTitle(screen) {
  if (screen.brand) return screen.brand;
  if (screen.address) return screen.address;
  return `Screen ${screen.screenId}`;
}

function screenDescription(screen) {
  if (screen.locationDescription) return screen.locationDescription;
  if (screen.address) return screen.address;
  return 'A premium INVERT screen in a high-traffic local venue.';
}

function NetworkScreenCard({ screen, isLive, isActive, onToggle, onLaunch }) {
  const title = screenTitle(screen);
  const description = screenDescription(screen);

  return (
    <article
      className={`relative rounded-xl overflow-hidden cursor-pointer group transition-all duration-300 ${
        isActive ? 'ring-2 ring-invert-orange scale-[1.02]' : 'hover:shadow-lg'
      }`}
      onClick={() => onToggle(screen.screenId)}
      onKeyDown={(e) => e.key === 'Enter' && onLaunch(screen.screenId)}
      tabIndex={0}
      role="button"
      aria-label={`${title}. ${description}`}
    >
          {isLive && (
        <span className="absolute top-3 right-3 z-10 px-2 py-1 rounded-full text-xs font-semibold bg-emerald-500/90 text-white">
          🟢 Live
        </span>
      )}
      <div className="aspect-[4/3] bg-slate-200">
        <img
          src={screen.listingPhotoUrl}
          alt={`INVERT screen at ${title}`}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>
      <div
        className={`absolute inset-0 bg-invert-navy/85 flex items-end p-6 transition-opacity duration-300 ${
          isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
      >
        <div>
          <h2 className="text-white font-semibold text-lg mb-1">{title}</h2>
          <p className="text-slate-300 text-sm mb-4">{description}</p>
          <button
            type="button"
            className="bg-invert-orange text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-invert-orange-deep transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onLaunch(screen.screenId);
            }}
          >
            Advertise here
          </button>
        </div>
      </div>
    </article>
  );
}

export default function Network() {
  const navigate = useNavigate();
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeScreenId, setActiveScreenId] = useState(null);
  const { screens: statusScreens, liveScreens } = useNetworkStatus();

  const liveByScreenId = useMemo(() => {
    const map = new Map();
    statusScreens.forEach((s) => {
      map.set(s.screenId, s.isLive);
    });
    return map;
  }, [statusScreens]);

  const loadScreens = useCallback(() => {
    setLoading(true);
    setError(null);
    getNetworkScreens()
      .then((data) => setScreens(Array.isArray(data.screens) ? data.screens : []))
      .catch((err) => {
        setError(err.message || 'Could not load the network gallery.');
        setScreens([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadScreens();
  }, [loadScreens]);

  const handleLaunch = (screenId) => {
    navigate(`/launch?screenId=${encodeURIComponent(screenId)}`);
  };

  return (
    <MarketingLayout>
      <section className="bg-invert-navy text-white pt-32 lg:pt-40 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-6">
              Network
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
              Intelligent screens at real venues
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-6">
              Browse placements across the INVERT network. Every screen is an AI-powered business display in a premium local venue.
            </p>
            {liveScreens > 0 && (
              <p className="text-sm text-emerald-400 font-medium mb-4">
                🟢 {liveScreens} screens live right now
              </p>
            )}
            <Link
              to="/proof"
              className="inline-block text-sm font-semibold text-invert-orange hover:text-invert-orange-deep transition-colors"
            >
              See full proof — partners, stats, live map →
            </Link>
          </FadeIn>
        </div>
      </section>

      <Section>
        {loading && (
          <div className="flex justify-center py-20" aria-live="polite">
            <RingLoader color="#dc5024" loading size={48} aria-label="Loading" />
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadScreens}>Try again</Button>
          </div>
        )}

        {!loading && !error && screens.length === 0 && (
          <p className="text-center text-slate-500 py-12">
            Gallery photos are coming soon as new screens join the network.
          </p>
        )}

        {!loading && !error && screens.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {screens.map((screen) => (
              <NetworkScreenCard
                key={screen.screenId}
                screen={screen}
                isLive={liveByScreenId.get(screen.screenId) ?? true}
                isActive={activeScreenId === screen.screenId}
                onToggle={(id) => setActiveScreenId((c) => (c === id ? null : id))}
                onLaunch={handleLaunch}
              />
            ))}
          </div>
        )}
      </Section>

      <Section className="bg-slate-50 text-center">
        <FadeIn className="max-w-xl mx-auto">
          <h2 className="text-2xl lg:text-3xl font-bold text-invert-navy mb-4">
            Want to advertise on more, or all?
          </h2>
          <p className="text-slate-600 mb-8">
            Reach the full INVERT network from your own advertiser dashboard.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button to="/advertiserSignup" size="lg">
              Create Advertiser Account
            </Button>
            <Button to="/proof" variant="secondary" size="lg">
              See Network Proof
            </Button>
          </div>
        </FadeIn>
      </Section>
    </MarketingLayout>
  );
}
